/**
 * Hash Map with reversed lookup
 */
export const createReversedMapObject = <
    PrimaryKey,
    SecondaryKey,
    InneObject extends {uuid: SecondaryKey},
>(
    initial?: Map<PrimaryKey, Map<SecondaryKey, InneObject>>,
) => {
    const map = new Map(initial);
    const reversed = new Map<SecondaryKey, PrimaryKey>();
    if (initial) {
        for (const [primaryKey, subMap] of initial) {
            for (const secondaryKey of subMap.keys()) {
                reversed.set(secondaryKey, primaryKey);
            }
        }
    }

    return Object.freeze({
        set(primaryKey: PrimaryKey, inneValue: InneObject) {
            reversed.set(inneValue.uuid, primaryKey);
            let subMap = map.get(primaryKey);
            if (!subMap) {
                subMap = new Map();
                map.set(primaryKey, subMap);
            }
            subMap.set(inneValue.uuid, inneValue);
            reversed.set(inneValue.uuid, primaryKey);
        },
        get(primaryKey: PrimaryKey, secondaryKey: SecondaryKey) {
            return map.get(primaryKey)?.get(secondaryKey);
        },
        has(primaryKey: PrimaryKey, secondaryKey: SecondaryKey) {
            return map.get(primaryKey)?.has(secondaryKey);
        },
        getReversed(secondKey: SecondaryKey) {
            return reversed.get(secondKey);
        },
        hasReversed(secondKey: SecondaryKey) {
            return reversed.has(secondKey);
        },
        values(primaryKey: PrimaryKey) {
            return map.get(primaryKey)?.values();
        },
        keys(primaryKey: PrimaryKey) {
            return map.get(primaryKey)?.keys();
        },
        entries(primaryKey: PrimaryKey) {
            return map.get(primaryKey)?.entries();
        },
        remove(primary: PrimaryKey, secondaryKey: SecondaryKey) {
            const deleted = map.get(primary)?.delete(secondaryKey);
            if (deleted) {
                return reversed.delete(secondaryKey);
            }
            return false;
        },
        primaryKeys() {
            return map.keys();
        },
        primaryValues() {
            return map.values();
        },
        primaryEntries() {
            return map.entries();
        },
        getByPrimary(primary: PrimaryKey) {
            return map.get(primary);
        },
        removeByPrimary(primaryKey: PrimaryKey) {
            const subMap = map.get(primaryKey);
            if (!subMap) {
                return;
            }
            for (const pid of subMap.keys()) {
                reversed.delete(pid);
            }
            return subMap.clear();
        },
        copyTo(to: Map<PrimaryKey, Map<SecondaryKey, InneObject>>) {
            for (const [k, v] of this.primaryEntries()) {
                const existing = to.get(k);
                if (existing) {
                    for (const [ik, iv] of v) {
                        existing.set(ik, iv);
                    }
                } else {
                    to.set(k, new Map(v));
                }
            }
        },
        clear() {
            map.clear();
            reversed.clear();
        },
    });
};
